"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@workspace/ui/components/alert-dialog";
import { Button } from "@workspace/ui/components/button";
import { Loader2 } from "lucide-react";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type SaveCallback = () => Promise<boolean>;
type DiscardCallback = () => void;
type NavigationMethod = "push" | "replace";

type PendingNavigation = {
  href: string;
  method: NavigationMethod;
};

type NavigationGuardContextValue = {
  setFormDirty: (id: string, isDirty: boolean) => void;
  setSaveCallback: (id: string, callback: SaveCallback | null) => void;
  setDiscardCallback: (id: string, callback: DiscardCallback | null) => void;
  navigate: (href: string, method?: NavigationMethod) => void;
  hasDirtyForms: boolean;
};

const NavigationGuardContext =
  createContext<NavigationGuardContextValue | null>(null);

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const dirtyFormsRef = useRef<Set<string>>(new Set());
  const saveCallbacksRef = useRef<Map<string, SaveCallback>>(new Map());
  const discardCallbacksRef = useRef<Map<string, DiscardCallback>>(new Map());
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveCallback, setHasSaveCallback] = useState(false);
  const [hasDirtyForms, setHasDirtyForms] = useState(false);

  const setFormDirty = useCallback((id: string, isDirty: boolean) => {
    if (isDirty) {
      dirtyFormsRef.current.add(id);
    } else {
      dirtyFormsRef.current.delete(id);
    }
    setHasDirtyForms(dirtyFormsRef.current.size > 0);
  }, []);

  const setSaveCallback = useCallback(
    (id: string, callback: SaveCallback | null) => {
      if (callback) {
        saveCallbacksRef.current.set(id, callback);
      } else {
        saveCallbacksRef.current.delete(id);
      }
      setHasSaveCallback(saveCallbacksRef.current.size > 0);
    },
    []
  );

  const setDiscardCallback = useCallback(
    (id: string, callback: DiscardCallback | null) => {
      if (callback) {
        discardCallbacksRef.current.set(id, callback);
      } else {
        discardCallbacksRef.current.delete(id);
      }
    },
    []
  );

  const executeNavigation = useCallback(
    ({ href, method }: PendingNavigation) => {
      router[method](href as Route);
    },
    [router]
  );

  const openNavigationConfirmation = useCallback(
    (navigation: PendingNavigation) => {
      const dirtyFormIds = Array.from(dirtyFormsRef.current);
      setHasSaveCallback(
        dirtyFormIds.length > 0 &&
          dirtyFormIds.every((id) => saveCallbacksRef.current.has(id))
      );
      setPendingNavigation(navigation);
    },
    []
  );

  const navigate = useCallback(
    (href: string, method: NavigationMethod = "push") => {
      const navigation = { href, method };
      if (dirtyFormsRef.current.size === 0) {
        executeNavigation(navigation);
        return;
      }
      openNavigationConfirmation(navigation);
    },
    [executeNavigation, openNavigationConfirmation]
  );

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyFormsRef.current.size > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (dirtyFormsRef.current.size === 0) {
        return;
      }

      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) {
        return;
      }

      const target = anchor.getAttribute("target");
      if (target && target.toLowerCase() !== "_self") {
        return;
      }

      if (anchor.hasAttribute("download")) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("blob:") ||
        href.startsWith("data:")
      ) {
        return;
      }

      if (anchor.origin !== window.location.origin) {
        return;
      }

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      openNavigationConfirmation({ href, method: "push" });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleClick, true);
    };
  }, [openNavigationConfirmation]);

  const handleDiscard = () => {
    if (!pendingNavigation) {
      return;
    }

    for (const id of dirtyFormsRef.current) {
      discardCallbacksRef.current.get(id)?.();
    }

    dirtyFormsRef.current.clear();
    setHasDirtyForms(false);
    setHasSaveCallback(false);
    executeNavigation(pendingNavigation);
    setPendingNavigation(null);
  };

  const handleSaveAsDraft = async () => {
    if (!pendingNavigation) {
      return;
    }

    setIsSaving(true);
    try {
      const callbacks = Array.from(dirtyFormsRef.current)
        .map((id) => saveCallbacksRef.current.get(id))
        .filter((callback): callback is SaveCallback => callback !== undefined);
      const results = await Promise.all(callbacks.map((cb) => cb()));
      const allSucceeded = results.every((result) => result === true);

      if (allSucceeded) {
        dirtyFormsRef.current.clear();
        setHasDirtyForms(false);
        setHasSaveCallback(false);
        executeNavigation(pendingNavigation);
        setPendingNavigation(null);
      } else {
        setPendingNavigation(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <NavigationGuardContext.Provider
      value={{
        setFormDirty,
        setSaveCallback,
        setDiscardCallback,
        navigate,
        hasDirtyForms,
      }}
    >
      {children}
      <AlertDialog
        onOpenChange={(open) =>
          !open && !isSaving && setPendingNavigation(null)
        }
        open={!!pendingNavigation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Descartar cambios?</AlertDialogTitle>
            <AlertDialogDescription>
              Tenés cambios sin guardar que se perderán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex-col flex">
            {hasSaveCallback && (
              <Button disabled={isSaving} onClick={handleSaveAsDraft}>
                {isSaving && <Loader2 className="animate-spin" />}
                Guardar como borrador
              </Button>
            )}
            <AlertDialogAction
              disabled={isSaving}
              onClick={handleDiscard}
              variant="destructive"
            >
              Descartar
            </AlertDialogAction>
            <AlertDialogCancel disabled={isSaving}>Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) {
    throw new Error(
      "useNavigationGuard must be used within NavigationGuardProvider"
    );
  }
  return ctx;
}
